import { useMemo } from 'react';

import { DebugStore } from '../store';

import { mockSettingsStorage } from './storage';
import type { QueryMockConfig } from './types';

export const MOCKING_ENABLED = import.meta.env.APP_JW_ENV === 'dev' || import.meta.env.APP_JW_ENV === 'stg';

// biome-ignore lint/suspicious/noExplicitAny: need any for TS to infer type
type RequestFn<Result> = (...args: any[]) => Promise<Result>;

export function mockRequest<Result = unknown, F extends RequestFn<Result> = RequestFn<Result>>(
  fn: F,
  mockConf?: QueryMockConfig<Result>,
): F {
  // Add mock conf to our global store so we can expose it in the mocks slide-out
  if (MOCKING_ENABLED && mockConf) {
    DebugStore.update((draft) => {
      draft.mockConfigs[mockConf.name] = mockConf;
    });
  }

  // biome-ignore lint/suspicious/noExplicitAny: need any for TS to infer type
  return (async (...args: any[]) => {
    // Retrieve the user's setting for this mock from storage
    const mockSetting = mockConf ? mockSettingsStorage.retrieve(mockConf) : undefined;

    // Run the regular function if mock is (globally) disabled
    if (!(MOCKING_ENABLED && mockSetting?.resourceName)) {
      return fn(...args);
    }

    // Fetch mock resources (file)
    const resources = typeof mockConf?.resources === 'function' ? await mockConf.resources() : mockConf?.resources;
    if (!resources?.[mockSetting.resourceName]) {
      throw new Error('Could not fetch Mock resources');
    }

    // Lookup the resource by the user's setting
    const resource = resources[mockSetting.resourceName];

    // Request delaying
    const delay = mockSetting.delay ?? resource.delay;
    if (delay) {
      await new Promise((r) => setTimeout(r, delay));
    }

    // Request erroring
    const err = mockSetting.error || resource.error;
    if (err) {
      throw err === true ? new Error('Unknown error') : err;
    }

    // Return dynamic response
    if (typeof resource.response === 'function') {
      // biome-ignore lint/suspicious/noExplicitAny: need any for TS to infer type
      return await (resource.response as (...args: any[]) => unknown)(
        // Use explicit request params if they were passed in the config, else default to this function's given args
        ...(mockConf?.requestParams ? [mockConf.requestParams] : args),
      );
    }

    // Return static response
    return resource.response;
  }) as F;
}

export function useMockRequest<Result = unknown, F extends RequestFn<Result> = RequestFn<Result>>(
  fn: F,
  mockConf?: QueryMockConfig<Result>,
): F {
  // Make sure the mock function gets refreshed when the user changes the mock setting
  const mockSetting = mockSettingsStorage.useValue(mockConf);
  // biome-ignore lint/correctness/useExhaustiveDependencies(mockSetting): memoize on this var
  // biome-ignore lint/correctness/useExhaustiveDependencies(fn): stable
  // biome-ignore lint/correctness/useExhaustiveDependencies(mockConf): stable
  return useMemo(() => mockRequest(fn, mockConf), [mockSetting]);
}
